<template>
  <!-- 图片带背景色：自定义 div+img 结构，确保底色必显（透明图片透出底色） -->
  <div v-if="imageWithBg" :class="['agent-avatar', 'agent-avatar-imgbox', shape === 'circle' ? 'is-circle' : 'is-square']" :style="imgBoxStyle">
    <img :src="imageUrl" class="agent-avatar-img" alt="" />
  </div>
  <!-- 内置头像 / 普通图片 / 空值：用 el-avatar -->
  <el-avatar v-else :size="size" :shape="shape" :src="imageUrl" class="agent-avatar">
    <div v-if="builtin" class="builtin-avatar" :style="{ background: builtin.bg, color: builtinTextColor }">
      <el-icon :size="Math.round(size * 0.5)"><component :is="iconComp" /></el-icon>
    </div>
    <template v-else>{{ (name || '?').charAt(0) }}</template>
  </el-avatar>
</template>

<script setup>
import { computed } from 'vue';
import { parseAgentAvatar } from '@/modules/huyu-framework/backend/utils/agentAvatars';
import {
  ChatDotRound, Reading, Document, Tools, Aim, Headset,
  TrendCharts, Brush, ChatLineSquare, EditPen, VideoCamera, MagicStick,
  Monitor, Microphone, Picture, Promotion, Avatar, Money,
  Notebook, FirstAidKit, Van, Connection, Lock, Cpu
} from '@element-plus/icons-vue';

const props = defineProps({
  // agentAvatar 值：内置标识（builtin:key）、图片 URL，或带背景色图片（url:hex:url）
  avatar: { type: String, default: '' },
  size: { type: Number, default: 40 },
  // 名称，用于无头像时的首字兜底
  name: { type: String, default: '' },
  shape: { type: String, default: 'circle' }
});

const baseUrl = import.meta.env.VITE_APP_BASE_API;

const iconMap = {
  ChatDotRound, Reading, Document, Tools, Aim, Headset,
  TrendCharts, Brush, ChatLineSquare, EditPen, VideoCamera, MagicStick,
  Monitor, Microphone, Picture, Promotion, Avatar, Money,
  Notebook, FirstAidKit, Van, Connection, Lock, Cpu
};

const parsed = computed(() => parseAgentAvatar(props.avatar));
const builtin = computed(() => (parsed.value && parsed.value.type === 'builtin' ? parsed.value : null));
const imageWithBg = computed(() => (parsed.value && parsed.value.type === 'image' ? parsed.value : null));
const iconComp = computed(() => (builtin.value ? iconMap[builtin.value.icon] : null));

// 浅色底（如白色/透明）时图标/文字用深色，保证可读
const isLightColor = (hex) => {
  if (!hex) return false;
  if (hex === 'transparent') return true;
  const h = hex.replace('#', '');
  if (h.length !== 6) return false;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b > 180;
};
const builtinTextColor = computed(() => (builtin.value && isLightColor(builtin.value.bg) ? '#606266' : '#ffffff'));

const imageUrl = computed(() => {
  if (builtin.value) return '';
  const raw = imageWithBg.value ? imageWithBg.value.url : props.avatar;
  if (!raw) return '';
  // 相对路径拼接 baseUrl；完整 URL / blob / data 直接使用
  if (/^(https?:|blob:|data:)/.test(raw)) return raw;
  return baseUrl + raw;
});

// 图片带背景色：自定义盒子的尺寸与底色（透明图片透出底色）
const imgBoxStyle = computed(() => (imageWithBg.value ? { width: props.size + 'px', height: props.size + 'px', background: imageWithBg.value.bg } : null));
</script>

<style scoped>
.agent-avatar {
  flex-shrink: 0;
  /* 细边框过渡，避免纯色底与页面背景割裂 */
  border: 1px solid #e4e7ed;
  /* 默认透明背景：内置头像由内部底色填充，上传图片保留透明通道 */
  --el-avatar-bg-color: transparent;
  --el-avatar-text-color: #909399;
}

.agent-avatar-imgbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
}

.agent-avatar-imgbox.is-circle {
  border-radius: 50%;
}

.agent-avatar-imgbox.is-square {
  border-radius: 4px;
}

.agent-avatar-img {
  display: block;
  width: 100%;
  height: 100%;
}

.builtin-avatar {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}
</style>
